import { useEffect, useState } from 'react';
import { X, Tag as TagIcon, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useProjects } from '@/hooks/use-projects';
import { useLists } from '@/hooks/use-lists';
import { tagSetsApi } from '@/lib/api';
import { getContrastTextColor } from '@/lib/utils';
import type { SearchFilters as SearchFiltersType } from '@/hooks/use-search';
import type { TagSet, Tag } from '@/types';

interface SearchFiltersProps {
  filters: SearchFiltersType;
  onFiltersChange: (filters: SearchFiltersType) => void;
  onClear: () => void;
}

export function SearchFilters({ filters, onFiltersChange, onClear }: SearchFiltersProps) {
  const { data: projects = [] } = useProjects();
  const { data: lists = [] } = useLists(filters.projectId);
  const [tagSets, setTagSets] = useState<TagSet[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    async function loadTags() {
      try {
        const response = await tagSetsApi.getAll();
        const sets = response.data;
        setTagSets(sets);

        const tagsPromises = sets.map((set) => tagSetsApi.getTags(set.id));
        const tagsResponses = await Promise.all(tagsPromises);
        const tags = tagsResponses.flatMap((r) => r.data);
        setAllTags(tags);
      } catch {
        // Ignore errors loading tags
      }
    }
    loadTags();
  }, []);

  const handleProjectChange = (value: string) => {
    onFiltersChange({
      ...filters,
      projectId: value === 'all' ? undefined : value,
      listId: undefined,
      statusId: undefined,
    });
  };

  const handleListChange = (value: string) => {
    onFiltersChange({
      ...filters,
      listId: value === 'all' ? undefined : value,
    });
  };

  const handleDueBeforeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dueBefore: e.target.value || undefined,
    });
  };

  const handleDueAfterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dueAfter: e.target.value || undefined,
    });
  };

  const handleUseDateClosedChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      useDateClosed: checked,
    });
  };

  const handleTagToggle = (tagId: string) => {
    const currentTags = filters.tagIds || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((id) => id !== tagId)
      : [...currentTags, tagId];
    onFiltersChange({
      ...filters,
      tagIds: newTags.length > 0 ? newTags : undefined,
    });
  };

  const selectedProject = projects.find((p) => p.id === filters.projectId);
  const statuses = selectedProject?.statuses || [];

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      statusId: value === 'all' ? undefined : value,
    });
  };

  const selectedTags = allTags.filter((tag) => filters.tagIds?.includes(tag.id));

  const handleFollowingOnlyChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      followingOnly: checked,
    });
  };

  const hasFilters = !!(
    filters.projectId ||
    filters.listId ||
    filters.statusId ||
    filters.assigneeId ||
    filters.dueBefore ||
    filters.dueAfter ||
    (filters.tagIds && filters.tagIds.length > 0) ||
    filters.followingOnly
  );

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Filters</h3>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={onClear} className="h-7 gap-1 text-xs">
            <X className="h-3 w-3" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Quick filter: Tickets I follow */}
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <Checkbox
          id="followingOnly"
          checked={filters.followingOnly || false}
          onCheckedChange={handleFollowingOnlyChange}
        />
        <Label
          htmlFor="followingOnly"
          className="text-sm font-medium cursor-pointer flex items-center gap-2"
        >
          <BellRing className="h-4 w-4 text-muted-foreground" />
          Tickets I follow
        </Label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="project" className="text-xs">
            Project
          </Label>
          <Select value={filters.projectId || 'all'} onValueChange={handleProjectChange}>
            <SelectTrigger id="project">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="list" className="text-xs">
            List
          </Label>
          <Select
            value={filters.listId || 'all'}
            onValueChange={handleListChange}
            disabled={!filters.projectId}
          >
            <SelectTrigger id="list">
              <SelectValue placeholder="All lists" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lists</SelectItem>
              {lists.map((list) => (
                <SelectItem key={list.id} value={list.id}>
                  {list.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status" className="text-xs">
            Status
          </Label>
          <Select
            value={filters.statusId || 'all'}
            onValueChange={handleStatusChange}
            disabled={!filters.projectId}
          >
            <SelectTrigger id="status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status.id} value={status.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    {status.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="dateAfter" className="text-xs">
              {filters.useDateClosed ? 'Closed after' : 'Due after'}
            </Label>
            <div className="flex items-center gap-1.5">
              <Checkbox
                id="useDateClosed"
                checked={filters.useDateClosed || false}
                onCheckedChange={handleUseDateClosedChange}
                className="h-3.5 w-3.5"
              />
              <Label htmlFor="useDateClosed" className="text-xs text-muted-foreground cursor-pointer">
                Date closed
              </Label>
            </div>
          </div>
          <Input
            id="dateAfter"
            type="date"
            value={filters.dueAfter || ''}
            onChange={handleDueAfterChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="dateBefore" className="text-xs">
            {filters.useDateClosed ? 'Closed before' : 'Due before'}
          </Label>
          <Input
            id="dateBefore"
            type="date"
            value={filters.dueBefore || ''}
            onChange={handleDueBeforeChange}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Tags</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-10"
              >
                <TagIcon className="mr-2 h-4 w-4" />
                {selectedTags.length > 0 ? (
                  <span className="truncate">
                    {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select tags...</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tagSets.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">No tags available</p>
                ) : (
                  tagSets.map((tagSet) => {
                    const tagsInSet = allTags.filter((t) => t.tagSetId === tagSet.id);
                    if (tagsInSet.length === 0) return null;
                    return (
                      <div key={tagSet.id} className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground px-2">
                          {tagSet.name}
                        </p>
                        <div className="flex flex-wrap gap-1 px-1">
                          {tagsInSet.map((tag) => {
                            const isSelected = filters.tagIds?.includes(tag.id);
                            const bgColor = tag.color || '#6b7280';
                            const textColor = getContrastTextColor(bgColor);
                            return (
                              <Badge
                                key={tag.id}
                                variant="secondary"
                                className="cursor-pointer text-xs font-medium"
                                style={{
                                  backgroundColor: isSelected ? bgColor : `${bgColor}30`,
                                  borderColor: 'transparent',
                                  color: isSelected ? textColor : bgColor,
                                }}
                                onClick={() => handleTagToggle(tag.id)}
                              >
                                {tag.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {selectedTags.map((tag) => {
            const bgColor = tag.color || '#6b7280';
            const textColor = getContrastTextColor(bgColor);
            return (
              <Badge
                key={tag.id}
                className="gap-1 text-xs font-medium"
                style={{
                  backgroundColor: bgColor,
                  color: textColor,
                  borderColor: 'transparent',
                }}
              >
                {tag.name}
                <X
                  className="h-3 w-3 cursor-pointer hover:opacity-70"
                  onClick={() => handleTagToggle(tag.id)}
                  style={{ color: textColor }}
                />
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
